import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, ReactNode } from 'react';

import { Avatar as RadixAvatar, useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';

type RadixAvatarProps = ComponentPropsWithoutRef<typeof RadixAvatar>;

export type AvatarSize =
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge'
  | 'xxxlarge'
  | 'huge'
  | 'massive';
export type AvatarStatus = 'online' | 'offline' | 'away';
export type AvatarCustomColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'destructive';
export type AvatarRadixColor =
  | 'gray'
  | 'mauve'
  | 'slate'
  | 'sage'
  | 'olive'
  | 'sand'
  | 'tomato'
  | 'red'
  | 'ruby'
  | 'crimson'
  | 'pink'
  | 'plum'
  | 'purple'
  | 'violet'
  | 'iris'
  | 'indigo'
  | 'blue'
  | 'cyan'
  | 'teal'
  | 'jade'
  | 'green'
  | 'grass'
  | 'brown'
  | 'orange'
  | 'sky'
  | 'mint'
  | 'lime'
  | 'yellow'
  | 'amber'
  | 'info'
  | 'gold'
  | 'bronze';
export type AvatarColor = AvatarCustomColor | AvatarRadixColor;
export type AvatarVariant =
  | 'contained'
  | 'outlined'
  | 'text'
  | 'classic'
  | 'soft'
  | 'surface'
  | 'ghost'
  | 'dashed';

interface BaseAvatarProps {
  src?: string;
  alt?: string;
  fallback?: ReactNode;
  size?: AvatarSize;
  status?: AvatarStatus;
  color?: AvatarColor;
  variant?: AvatarVariant;
  className?: string;
  radius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  highContrast?: boolean;
  children?: ReactNode;
}

export type AvatarProps = Omit<
  RadixAvatarProps,
  'size' | 'radius' | 'color' | 'variant' | 'fallback' | 'highContrast'
> &
  BaseAvatarProps;

const sizeMapping = {
  xsmall: '1',
  small: '2',
  medium: '3',
  large: '4',
  xlarge: '5',
  xxlarge: '6',
  xxxlarge: '7',
  huge: '8',
  massive: '9',
} as const;

const radiusMapping = {
  none: 'none',
  small: 'small',
  medium: 'medium',
  large: 'large',
  full: 'full',
} as const;

const variantMapping = {
  classic: 'solid', // Will be styled via CSS with gradient
  contained: 'solid',
  outlined: 'soft', // Will be styled via CSS
  text: 'soft', // Will be styled via CSS
  soft: 'soft',
  ghost: 'soft', // Will be styled via CSS
  surface: 'soft', // Will be styled via CSS
  dashed: 'soft', // Will be styled via CSS
} as const;

function resolveColor(
  color: AvatarColor,
  variant: AvatarVariant,
  accentColor?: string
): {
  color: RadixAvatarProps['color'];
  highContrast?: boolean;
} {
  const allowedColors = [
    'gray',
    'tomato',
    'red',
    'ruby',
    'crimson',
    'pink',
    'plum',
    'purple',
    'violet',
    'iris',
    'indigo',
    'blue',
    'cyan',
    'teal',
    'jade',
    'green',
    'grass',
    'brown',
    'orange',
    'gold',
    'bronze',
    'yellow',
    'amber',
  ];
  if (
    [
      'default',
      'primary',
      'secondary',
      'success',
      'warning',
      'error',
      'info',
      'destructive',
    ].includes(color as string)
  ) {
    switch (color) {
      case 'primary':
        return {
          color: allowedColors.includes(accentColor || '')
            ? (accentColor as RadixAvatarProps['color'])
            : 'indigo',
          highContrast: variant === 'classic' ? true : false,
        };
      case 'secondary':
        // Secondary should always be gray with highContrast (almost black)
        return { color: 'gray', highContrast: true };
      case 'default':
        // Default: gray without highContrast for classic (will be styled via CSS with light background)
        // For contained: gray without highContrast (will be styled via CSS with white background)
        if (variant === 'classic' || variant === 'contained') {
          return { color: 'gray' };
        }
        return { color: 'gray' };
      case 'success':
        return { color: 'jade' };
      case 'warning':
        return { color: 'amber' };
      case 'error':
        return { color: 'crimson' };
      case 'destructive':
        return { color: 'ruby' };
      case 'info':
        return { color: 'cyan' };
      default:
        return { color: 'gray' };
    }
  }
  return { color: color as RadixAvatarProps['color'] };
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  (
    {
      src,
      alt,
      fallback,
      size = 'medium',
      status,
      color = 'default',
      variant = 'contained',
      radius = 'full',
      className,
      highContrast,
      children,
      ...props
    },
    ref
  ) => {
    const mappedSize = sizeMapping[size] || '3';
    const mappedRadius = radiusMapping[radius] || 'full';
    const mappedVariant =
      variantMapping[variant as NonNullable<BaseAvatarProps['variant']>] ||
      'solid';
    const theme = useThemeContext();
    const accentColor = theme?.accentColor || 'indigo';
    const { color: resolvedColor, highContrast: resolvedContrast } =
      resolveColor(color, variant, accentColor);
    const fallbackValue =
      fallback != null ? fallback : !src && children ? children : '';
    const statusClass = status ? `avatar--status-${status}` : undefined;
    return (
      <RadixAvatar
        ref={ref}
        src={src}
        alt={alt}
        fallback={fallbackValue}
        size={mappedSize}
        radius={mappedRadius}
        variant={mappedVariant}
        color={resolvedColor}
        highContrast={
          typeof highContrast === 'boolean' ? highContrast : resolvedContrast
        }
        className={clsx(
          'avatar',
          `avatar-size-${size}`,
          `avatar-color-${color}`,
          `avatar-variant-${variant}`,
          className,
          statusClass
        )}
        {...props}
      />
    );
  }
);
Avatar.displayName = 'Avatar';
